import { Button } from "@/components/ui";
import { RootState } from "@/redux/store";
import { Bookmark, ChevronDownIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { POC_DEPARTMENT, POC_DESIGNATION, POC_EMAIL, POC_NAME, POC_PHONE, SchoolPoc } from "./consts";
import { useAddEditSchoolPoc } from "./useAddEditSchoolPoc";
import { LOGIN_ROLES } from "@/lib/utils";

const AddEditSchoolPoc = () => {
  const {
    schoolPocs,
    isLoadingPocs,
    isUpdatingPocs,
    localPocs,
    isAddingNew,
    validationErrors,
    handleSaveChanges,
    handleAddNewRow,
    handleDeletePocLocal,
    handlePocFieldChange,
    handleCancelNewRow,
  } = useAddEditSchoolPoc();

  const [isExpanded, setIsExpanded] = useState(true);

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);

  if (isLoadingPocs) {
    return (
      <div className="bg-white p-5">
        <h2 className="text-[18px] text-primary mb-5">School POC Details</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading POC data...</div>
        </div>
      </div>
    );
  }

  const restrictedRoles = [LOGIN_ROLES.SALES_OPS, LOGIN_ROLES.INVENTORY_TEAM, LOGIN_ROLES.LABS_TEAM];
  const canShowActionButton = !restrictedRoles.some(role => hasRole(role));

  return (
    <div className="bg-white p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[18px] text-primary mt-10">School POC Details</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
          >
            <span>Collapse</span>
            <ChevronDownIcon className="w-4 h-4" />
          </button>
          {canShowActionButton && (
            <Button
              onClick={handleSaveChanges}
              variant="default"
              disabled={isUpdatingPocs}
              title="Save Changes"
            >
              Save
            </Button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          {/* POC Form Rows */}
          {localPocs.length > 0 ? (
            <div className="space-y-4">
              {localPocs.map((poc: SchoolPoc, index: number) => (
                <div
                  key={poc.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Name *
                      </label>
                      <input
                        type="text"
                        value={poc.pocName}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_NAME,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_NAME}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={hasRole("INVENTORY_TEAM")}
                      />
                      {validationErrors[`${poc.id}-${POC_NAME}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_NAME}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Designation *
                      </label>
                      <input
                        type="text"
                        value={poc.pocDesignation}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_DESIGNATION,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_DESIGNATION}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={hasRole("INVENTORY_TEAM")}
                      />
                      {validationErrors[`${poc.id}-${POC_DESIGNATION}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_DESIGNATION}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Department
                      </label>
                      <input
                        type="text"
                        value={poc.pocDepartment || ""}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_DEPARTMENT,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_DEPARTMENT}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={hasRole("INVENTORY_TEAM")}
                      />
                      {validationErrors[`${poc.id}-${POC_DEPARTMENT}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_DEPARTMENT}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Phone *
                      </label>
                      <input
                        type="text"
                        value={poc.pocPhone}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_PHONE,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_PHONE}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={hasRole("INVENTORY_TEAM")}
                      />
                      {validationErrors[`${poc.id}-${POC_PHONE}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_PHONE}`]}
                        </p>
                      )}
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          POC - Email *
                        </label>
                        <input
                          type="text"
                          value={poc.pocEmail}
                          onChange={(e) =>
                            handlePocFieldChange(
                              poc.id!,
                              POC_EMAIL,
                              e.target.value
                            )
                          }
                          placeholder="Please enter"
                          className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                            validationErrors[`${poc.id}-${POC_EMAIL}`]
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:ring-blue-500"
                          }`}
                          disabled={hasRole("INVENTORY_TEAM")}
                        />
                        {validationErrors[`${poc.id}-${POC_EMAIL}`] && (
                          <p className="text-red-500 text-xs mt-1">
                            {validationErrors[`${poc.id}-${POC_EMAIL}`]}
                          </p>
                        )}
                      </div>
                      {canShowActionButton && 
                      <button
                        onClick={() => handleDeletePocLocal(poc.id!)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Delete POC"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No POCs added yet. {hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN) ? 'Click "Add More" to add your first POC.' : ''}
            </div>
          )}

          {/* Add More Button */}
          {canShowActionButton && 
          <div className="flex justify-start">
            <Button
              onClick={handleAddNewRow}
              className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90"
            >
              <PlusIcon className="w-4 h-4" />
              Add More
            </Button>
          </div>
          }
        </div>
      )}
    </div>
  );
};

export default AddEditSchoolPoc;
